/* eslint-disable no-restricted-syntax */
// avoiding package root import to avoid circular dependency
// eslint-disable-next-line monorepo/no-internal-import
import {Locale} from '@aboutyou/config/src/locale';
import {getStaticRoute_UNSAFE} from './localizedUrls/getStaticRoute';
import {
  getPageComponentKeysByLocale_UNSAFE,
  getStaticPagesByLocale_UNSAFE,
} from './localizedUrls/legal-pages';
import {LEGAL_PAGE_TYPE} from './localizedUrls/legalPagesRouteKeys';
import {RouteKey} from './localizedUrls/routeKeys';
import {getSpecialSlugsByLocale_UNSAFE} from './localizedUrls/specialSlugs';
import {getTopCategorySlugsByLocale_UNSAFE} from './localizedUrls/topCategorySlugs';
import {TEASER_PAGE_PATHNAME} from './static-pathnames';
import {PageTypes} from './types';

export type PageTypePathMap = {
  [K in Exclude<PageTypes, '404'>]: readonly string[];
};

export const getPageTypePathsMap_UNSAFE = ({
  locale,
}: {
  locale: Locale;
}): PageTypePathMap => {
  const {children, female, male} = getTopCategorySlugsByLocale_UNSAFE(locale);
  const staticPageUrls = getStaticPagesByLocale_UNSAFE(locale);
  const pageComponentKeys = getPageComponentKeysByLocale_UNSAFE(locale);
  const specialUrls = getSpecialSlugsByLocale_UNSAFE(locale);

  const yourShopUrl = getStaticRoute_UNSAFE(RouteKey.YourShop, locale);
  const wishlistUrl = getStaticRoute_UNSAFE(RouteKey.Wishlist, locale);
  const brandsUrl = getStaticRoute_UNSAFE(RouteKey.Brands, locale);
  const basketUrl = getStaticRoute_UNSAFE(RouteKey.Basket, locale);

  return {
    account: ['/a/account'],
    accountOverview: ['/a/account-overview'],
    userProfile: ['/a/profile'],
    adventsCalendar: ['/adventskalender'],
    // product reviews and similar should come before ADP route as the first match wins and they share the same pattern
    similarProducts: ['/p/:brand/:prefixedProductId/similar'],
    productReviews: ['/p/:brand/:prefixedProductId/product-reviews'],
    articleDetail: ['/p/:brand/:prefixedProductId'],
    articleDetailSoldOut: ['/product-unavailable/:productId'],
    bankData: ['/a/bank-data', '/h/bank-data'],
    basket: [basketUrl],
    brandShop: ['/b/shop/*-:brandId', '/b/shop/*-:brandId/all', '/b/shop/*'],
    brandLegacy: ['/about/brand/:brandName', '/about/brand/:brandName/similar'],
    brandsOverview: [brandsUrl],
    chatbot: ['/a/chatbot'],
    couponLandingPage: ['/cs/:promotionId', '/cs'],
    couponWallet: ['/cw'],
    coinsWallet: ['/coins'],
    coinsHistory: ['/coins-history'],
    coinsStatus: ['/coins-status'],
    customPage: [`/cp/*-:pageId`, `/cp/*`],
    aboutUs: [`/about-aboutyou`],
    story: ['/s/:prefixedStoryId'],
    // story overview needs to come before categories as the first match wins and they share the same pattern
    storyOverview: [
      `/${male}/stories`,
      `/${female}/stories`,
      `/${children}/stories`,
    ],
    storyCluster: ['/sc/:storyClusterId'],
    outfitDetail: [`/o/:prefixedOutfitId`],
    // outfit overview needs to come before categories as the first match wins and they share the same pattern
    outfitOverview: [
      `/${male}/outfits`,
      `/${female}/outfits`,
      `/${children}/outfits`,
      `/${male}/outfits/*`,
      `/${female}/outfits/*`,
      `/${children}/outfits/*`,
    ],
    premium: [
      `/c/${male}/premium-:categoryId([^]*)?`,
      `/c/${female}/premium-:categoryId([^]*)?`,
    ],
    categoryLegacy: [
      `/${children}`,
      `/${male}`,
      `/${female}`,
      `/${children}/*`,
      `/${male}/*`,
      `/${female}/*`,
    ],
    category: [`/c/*-:categoryId`, `/c/*`],

    // orderSuccess should be above checkout to take precedence in router switch
    orderSuccess: [`/checkout/success`],
    // Important for Tracking - use only `/checkout` route
    // the translated URLs ( eg /kasse ) have to redirecto -> to /checkout
    checkout: ['/checkout'],
    DEV_appCheckoutUrl: ['/app-checkout-url'],

    dropsOverview: ['/d/:droptype/overview'],
    dropDetail: ['/d/:droptype/:dropId'],
    favourites: ['/a/favourites'],

    campaignConfirmationPage: ['/h/campaign-confirmation-page'],
    giftCardRefund: ['/h/giftcard-refund-form'],

    // These are the new self-service flow help center pages
    helpCenterArticlePage: ['/h/ssf/:sectionId/q-:articleId'],
    declarationOfLoss: ['/h/declaration-of-loss/:ticketId/:orderId'],
    legalReport: ['/h/legal-report/:productId'],
    helpCenterSectionPage: ['/h/ssf/:sectionId'],
    // Need this for redirection to old HC landing page
    helpCenterSectionRedirect: ['/h/ssf'],

    // Below are the legacy help center pages only
    contacts: [`/h/${specialUrls.contact}`],
    helpCenterSearchResultPageLegacy: ['/h/search'],
    helpCenterArticlePageLegacy: ['/h/:sectionName/q-:articleId'],
    helpCenterSectionPageLegacy: ['/h/:sectionName-:sectionId'],
    helpCenterLandingPageLegacy: ['/h'],

    notFound: ['/not-found'],

    // newsletterSuccess should be above newsletter to take precedence in router switch
    newsletterSubscription: ['/newsletter/opt-in'],
    newsletterSuccess: ['/newsletter/success'],
    newsletter: ['/newsletter'],

    payOrderApp: ['/a/orders/pay-now'],
    orderReturn: ['/a/orders/:orderId/return'],
    orderDetails: ['/a/orders/:orderId'],
    ordersOverview: ['/a/orders'],
    passwordReset: ['/a/password-reset'],
    searchResults: ['/search/:term'],
    search: ['/search'],
    seoStream: ['/t/:slug'],
    settingsChooseShop: ['/a/settings/shop'],
    settings: ['/a/settings'],
    static: [
      `/${staticPageUrls[LEGAL_PAGE_TYPE.Imprint]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.Privacy]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsOfUse]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsAndConditions]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsAndConditionsDP]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsAndConditionsFB1]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsAndConditionsFB2]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsAndConditionsIG]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsAndConditionsNPS]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsAndConditionsTiktok]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsOfUseReviews]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.GeneralConditionsReverseSupply]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.Cookies]}`,
      `/${staticPageUrls[LEGAL_PAGE_TYPE.TermsOfUseBestPriceGuarantee]}`,
      `/${LEGAL_PAGE_TYPE.Accessibility}`,
    ],
    streetwear: ['/hub/streetwear'],
    sportswear: ['/hub/sportswear'],
    legal: [
      `/${pageComponentKeys.aboutUs}`,
      `/${pageComponentKeys.campaignConditions}`,
    ],
    subscriptionConfirmation: ['/subscription-confirmation'],
    unsubscribeLandingPage: ['/remove-subscription/:token/:permission'],
    unsubscribeConfirmationLandingPage: ['/remove-subscription-confirmation'],
    trackAndTrace: ['/a/track-and-trace/email'],
    voucherPage: ['/gutscheine-rabatte'],
    welcome: [yourShopUrl],
    wishlist: [wishlistUrl],
    liveShopping: ['/l/live-shopping'],
    teaserPage: [TEASER_PAGE_PATHNAME],
    home: ['/'],
    blackFridayPage: ['/black-friday'],
  } as const;
};
